import * as THREE from 'three';
import { subtitlePlate } from '@vrlearning/lesson-schema';
import { PX_PER_METRE, fitText, type FittedText, type MeasureText } from '../engine/text.js';

/**
 * The subtitle plate (ADR-035; voice-guide design, H3): when the voice guides
 * a step, the lectern panel's body is hidden and the sentence being spoken
 * shows on a plate just above the navigation buttons (lesson-schema's
 * `subtitlePlate`), in the panel's plane and tilt. At most two lines, 56 px at
 * 1024 px/m (5.5 cm, about 1.7° at 1.9 m), shrinking to 44 px; a sentence
 * that still does not fit is paged (engine/guide.ts, `pages`). White on the
 * ADR-020 charcoal at 85%, with 3 cm corners.
 *
 * Pages crossfade, 0.15 s out and 0.15 s in, by opacity in the frame loop;
 * the plate fades in with its first line and out 0.3 s after the last. The
 * canvas is redrawn only when the page changes, and nothing is allocated per
 * frame.
 */

export const SUBTITLE_TEXT = {
  fontSize: 56,
  minFontSize: 44,
  lines: 2,
  lineHeight: 1.25,
  /** Canvas pixels between the plate's edge and its words. */
  pad: 36,
} as const;

/** The ADR-020 charcoal (index.html's `--bg`), at 85%. */
const PLATE_BACKGROUND = 'rgba(28, 29, 34, 0.85)';
const PLATE_TEXT = '#ffffff';
/** While the voice loads: the step's title, dimmed. */
const PLATE_DIM = '#a2a6b3';
/** 3 cm corners. */
const CORNER_METRES = 0.03;
/** Metres the plate stands proud of the (hidden) panel's plane (ui.ts places it). */
export const PLATE_PROUD = 0.001;

/** A page's crossfade, each way; and the plate's own fade out after the last line. */
export const PAGE_FADE_SECONDS = 0.15;
export const PLATE_FADE_OUT_SECONDS = 0.3;

const PLATE = subtitlePlate();
const CANVAS_WIDTH = Math.round(PLATE.width * PX_PER_METRE);
const CANVAS_HEIGHT = Math.round(PLATE.height * PX_PER_METRE);

/**
 * The words fitted to the plate: wrapped, from 56 px down to 44, within two
 * lines. `fits` is false when two lines at 44 px are not enough: then the
 * sentence is paged.
 */
export function fitSubtitle(
  text: string,
  measure: MeasureText,
): FittedText & { readonly fits: boolean } {
  const { fontSize, minFontSize, lines, lineHeight, pad } = SUBTITLE_TEXT;
  const fitted = fitText(
    text,
    {
      maxWidth: CANVAS_WIDTH - 2 * pad,
      maxHeight: lines * fontSize * lineHeight,
      fontSize,
      minFontSize,
      lineHeight,
      bold: true,
    },
    measure,
  );
  return { ...fitted, fits: fitted.lines.length <= lines };
}

/** What the plate should say: a page, or the title dimmed while the voice loads. */
export type PlateContent = { readonly text: string; readonly dim: boolean };

/**
 * The plate's fades, apart from the canvas so they can be tested: where its
 * opacity is heading, and when to draw the next page (at the bottom of the
 * crossfade, or at once when the plate was empty).
 */
export class PlateFader {
  #target: PlateContent | undefined;
  #drawn: PlateContent | undefined;
  #opacity = 0;

  get opacity(): number {
    return this.#opacity;
  }

  /** What is drawn now. */
  get drawn(): PlateContent | undefined {
    return this.#drawn;
  }

  set(content: PlateContent | undefined): void {
    this.#target = content;
  }

  /** Moves the fade on; returns what to draw when a new page is due, once. */
  update(dt: number): PlateContent | undefined {
    const target = this.#target;
    const same =
      target !== undefined &&
      this.#drawn !== undefined &&
      target.text === this.#drawn.text &&
      target.dim === this.#drawn.dim;
    if (same || (target === undefined && this.#drawn === undefined)) {
      if (this.#drawn !== undefined && this.#opacity < 1) {
        this.#opacity = Math.min(1, this.#opacity + dt / PAGE_FADE_SECONDS);
      }
      return undefined;
    }
    // Empty: the first line comes in at once and fades up.
    if (this.#drawn === undefined || this.#opacity <= 0) {
      this.#opacity = 0;
      this.#drawn = target;
      return target;
    }
    // A new page, or none: fade the old one out first.
    const out = target === undefined ? PLATE_FADE_OUT_SECONDS : PAGE_FADE_SECONDS;
    this.#opacity = Math.max(0, this.#opacity - dt / out);
    if (this.#opacity > 0) return undefined;
    this.#drawn = target;
    return target;
  }
}

function canvasMeasure(ctx: CanvasRenderingContext2D): MeasureText {
  return (text, fontSize, bold) => {
    ctx.font = `${bold ? 'bold ' : ''}${fontSize}px system-ui, sans-serif`;
    return ctx.measureText(text).width;
  };
}

export class SubtitlePlate {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas: HTMLCanvasElement;
  readonly #ctx: CanvasRenderingContext2D;
  readonly #texture: THREE.CanvasTexture;
  readonly #measure: MeasureText;
  readonly #fader = new PlateFader();

  constructor() {
    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_WIDTH;
    this.#canvas.height = CANVAS_HEIGHT;
    this.#ctx = this.#canvas.getContext('2d')!;
    this.#measure = canvasMeasure(this.#ctx);
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4;
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(PLATE.width, PLATE.height),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
    // Where it goes is ui.ts's to say, from lesson-schema's `subtitlePlate`.
    this.mesh.visible = false;
    this.mesh.name = 'subtitle-plate';
  }

  /** Whether `text` fits on two lines at the smallest size (for `pages`). */
  fits = (text: string): boolean => fitSubtitle(text, this.#measure).fits;

  /** What it should say; undefined fades it out. */
  set(content: PlateContent | undefined): void {
    this.#fader.set(content);
  }

  /** What it says now (drawn, fading or not). */
  get text(): string | undefined {
    return this.#fader.drawn?.text;
  }

  /** Once a frame: the fades, and a redraw only when the page changes. */
  update(dt: number): void {
    const draw = this.#fader.update(dt);
    if (draw) this.#draw(draw);
    const opacity = this.#fader.opacity;
    this.mesh.material.opacity = opacity;
    this.mesh.visible = opacity > 0 && this.#fader.drawn !== undefined;
  }

  #draw(content: PlateContent): void {
    const ctx = this.#ctx;
    const { width: w, height: h } = this.#canvas;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = PLATE_BACKGROUND;
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, CORNER_METRES * PX_PER_METRE);
    ctx.fill();

    const fitted = fitSubtitle(content.text, this.#measure);
    const lineHeight = fitted.fontSize * SUBTITLE_TEXT.lineHeight;
    const shown = fitted.lines.slice(0, SUBTITLE_TEXT.lines);
    const top = h / 2 - ((shown.length - 1) * lineHeight) / 2;
    ctx.fillStyle = content.dim ? PLATE_DIM : PLATE_TEXT;
    ctx.font = `bold ${fitted.fontSize}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    shown.forEach((line, i) => ctx.fillText(line, w / 2, top + i * lineHeight));
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
    this.#texture.needsUpdate = true;
  }

  dispose(): void {
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
